import { ArrowRight } from 'lucide-react';
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface PackActivateHeroProps {
  brand?: string;
  onJump: (id: string) => void;
  showInvite?: boolean;
}

const MOVES = [
  {
    id: 'activation-assets',
    genieIcon: 'clipboard-check' as const,
    title: 'Download the complete launch pack',
    desc: 'Printer files, launch copy, QR codes and digital assets in one checked bundle.',
    cta: 'Build my pack',
  },
  {
    id: 'activate-app',
    genieIcon: 'phone' as const,
    title: 'Get people on the app',
    desc: 'One install-first link and poster that puts this Genie on every phone.',
    cta: 'Activate mobile',
  },
  {
    id: 'activate-presentation',
    genieIcon: 'play' as const,
    title: 'Share the tailored presentation',
    desc: 'A customer-ready walkthrough shaped around this Genie, its audience, and its job.',
    cta: 'Preview & send',
  },
  {
    id: 'activate-signature',
    genieIcon: 'mail' as const,
    title: 'Add the email signature widget',
    desc: 'Turn every team email into a direct path to talk to or install this Genie.',
    cta: 'Copy the widget',
  },
];

const INVITE_MOVE = {
  id: 'activate-invite',
  genieIcon: 'lock' as const,
  title: 'Give people login access',
  desc: 'Grant specific people a HelpGenie login to view and manage this Genie in the dashboard.',
  cta: 'Manage access',
};

/**
 * The lead "How to activate" block — power moves, front and centre. Each
 * card jumps to the section that does the work.
 */
export const PackActivateHero = ({ brand = '#4E9CFF', onJump, showInvite }: PackActivateHeroProps) => {
  const moves = showInvite ? [...MOVES, INVITE_MOVE] : MOVES;
  return (
  <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
    <div className="rounded-[14px] bg-white p-6 md:p-8">
      <div className="text-center mb-6">
        <h2 className="text-2xl md:text-3xl font-bold text-[#222B45]">How to activate</h2>
        <p className="text-[#64748B] mt-1">Power moves to get your Genie in front of people.</p>
      </div>

      <div className={`grid gap-4 md:grid-cols-2 ${showInvite ? 'xl:grid-cols-5' : 'lg:grid-cols-4'}`}>
        {moves.map((m, i) => (
          <button
            key={m.id}
            type="button"
            onClick={() => onJump(m.id)}
            className="group relative text-left rounded-2xl border border-gray-200 p-5 transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_40px_rgba(15,23,42,0.12)] hover:border-transparent overflow-hidden"
          >
            {/* subtle gradient wash on hover */}
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
              style={{ background: `linear-gradient(135deg, ${brand}08, #FF4DB808)` }}
            />
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${brand}12` }}>
                  <GenieIcon name={m.genieIcon} size={26} color={brand} secondaryColor="#FF4DB8" />
                </div>
                <span className="text-2xl font-bold text-gray-200">{i + 1}</span>
              </div>
              <div className="font-semibold text-gray-900 mb-1 leading-snug">{m.title}</div>
              <p className="text-sm text-gray-600 mb-3 leading-snug">{m.desc}</p>
              <div className="text-sm font-medium flex items-center gap-1" style={{ color: brand }}>
                {m.cta}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  </div>
  );
};
